import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Send, Users, Bell, BellOff, Package, FolderTree, UserCircle2, Loader2, Check,
  AlertTriangle, Eye, MousePointerClick, CalendarClock, Zap, Repeat,
} from "lucide-react";
import usePushFeatures from "../../hooks/usePushFeatures";
import {
  ACTIVE_STATUSES, JobStatusBadge, ReviewDialog, ScheduledList, apiErrorMessage, jobIssue, jobOutcome,
} from "../../components/admin/PushScheduling";
import {
  EMPTY_RECURRENCE, RecurrencePicker, RecurringList, StoreTimeZone, recurrencePayload,
} from "../../components/admin/PushRecurring";
import {
  MAX_HORIZON_DAYS, MIN_LEAD_SECONDS, browserTimeZone, formatInZone, maxInputValue,
  minInputValue, todayInZone, validateRecurrence, validateSchedule,
} from "../../lib/scheduleTime";

const SEGMENTS = [
  { key: "all_customers", label: "All customers", icon: Users, needsParam: false, helper: "Customers who have a cart, wishlist or order in your store" },
  { key: "all_sellers", label: "All sellers", icon: Users, needsParam: false, helper: "Every seller account" },
  { key: "all_admins", label: "All admins", icon: Users, needsParam: false, helper: "All admin accounts" },
  { key: "wishlisters_of_product", label: "Wishlisters of a product", icon: Package, needsParam: true, paramKind: "product", helper: "Users who wishlisted a specific product" },
  { key: "wishlisters_of_category", label: "Wishlisters of a category", icon: FolderTree, needsParam: true, paramKind: "category", helper: "Users who wishlisted any product in a category" },
  { key: "creator_followers", label: "Followers of a creator", icon: UserCircle2, needsParam: true, paramKind: "creator", helper: "Users following a specific creator/seller" },
];
const SEGMENT_LABEL = { ...Object.fromEntries(SEGMENTS.map((s) => [s.key, s.label])), dormant_customers: "Dormant customers" };

// Idempotency key for the draft being confirmed. Kept in sessionStorage so a
// retry after a network failure — or after the admin refreshes mid-request —
// replays the same request instead of sending the notification twice.
const IDEM_STORE = "kb_push_idem";
const IDEM_TTL_MS = 10 * 60 * 1000;

function newKey() {
  try {
    if (window.crypto?.randomUUID) return `push-${window.crypto.randomUUID()}`;
  } catch { /* insecure context */ }
  return `push-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function idempotencyKeyFor(signature) {
  try {
    const saved = JSON.parse(sessionStorage.getItem(IDEM_STORE) || "null");
    if (saved && saved.sig === signature && Date.now() - saved.at < IDEM_TTL_MS) return saved.key;
  } catch { /* storage unavailable */ }
  const key = newKey();
  try { sessionStorage.setItem(IDEM_STORE, JSON.stringify({ sig: signature, key, at: Date.now() })); } catch { /* ignore */ }
  return key;
}

function clearIdempotencyKey() {
  try { sessionStorage.removeItem(IDEM_STORE); } catch { /* ignore */ }
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-[hsl(var(--kb-border))] p-4 sm:p-5 min-w-0" data-testid={`push-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-[hsl(var(--kb-muted))] text-xs uppercase tracking-wide mb-1">
        <Icon size={14} className="shrink-0" /> <span className="truncate">{label}</span>
      </div>
      <div className="font-display text-2xl sm:text-3xl">{value}</div>
    </div>
  );
}

function DisabledPanel() {
  return (
    <div className="bg-white border border-[hsl(var(--kb-border))] p-6 sm:p-10 text-center max-w-2xl" data-testid="push-features-disabled">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[hsl(var(--kb-surface-alt))] mb-4">
        <BellOff size={24} />
      </div>
      <h2 className="font-display text-2xl">Notifications aren't enabled for your store</h2>
      <p className="text-sm text-[hsl(var(--kb-muted))] mt-2">
        Bulk and scheduled notifications are switched on by the platform team.
        Contact support if you'd like to use them.
      </p>
    </div>
  );
}

function WhenPicker({ features, mode, setMode, value, setValue, schedule, timeZone, now, zone, recurring }) {
  const options = [
    features.bulk && { key: "now", label: "Send now", icon: Zap, testid: "push-when-now" },
    features.scheduled && { key: "schedule", label: "Schedule once", icon: CalendarClock, testid: "push-when-schedule" },
    features.scheduled && { key: "repeat", label: "Repeat", icon: Repeat, testid: "push-when-repeat" },
  ].filter(Boolean);
  const limits = features.limits || {};
  const cols = { 1: "grid-cols-1", 2: "grid-cols-1 sm:grid-cols-2", 3: "grid-cols-1 sm:grid-cols-3" }[options.length];
  return (
    <div data-testid="push-when">
      <label className="text-sm font-medium">When</label>
      <div className={`grid gap-2 mt-2 ${cols}`}>
        {options.map((o) => {
          const Icon = o.icon;
          const active = mode === o.key;
          return (
            <button key={o.key} type="button" onClick={() => setMode(o.key)} data-testid={o.testid} aria-pressed={active}
              className={`flex items-center gap-2 border p-3 text-left text-sm transition min-h-[44px] ${active ? "border-[hsl(var(--kb-primary))] bg-[hsl(var(--kb-primary))]/5 font-medium" : "border-[hsl(var(--kb-border))] hover:border-[hsl(var(--kb-primary))]/40"}`}>
              <Icon size={16} className="shrink-0" /> {o.label}
            </button>
          );
        })}
      </div>
      {(mode === "schedule" || mode === "repeat") && <div className="mt-3">{zone}</div>}
      {mode === "repeat" && recurring}
      {mode === "schedule" && (
        <div className="mt-3">
          <label htmlFor="push-schedule-input" className="text-sm font-medium">Date & time</label>
          <input
            id="push-schedule-input" type="datetime-local" step={60}
            className="block w-full max-w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1 bg-white min-h-[44px]"
            value={value} onChange={(e) => setValue(e.target.value)}
            min={minInputValue(now, limits.min_lead_seconds ?? MIN_LEAD_SECONDS, timeZone)}
            max={maxInputValue(now, limits.max_horizon_days ?? MAX_HORIZON_DAYS, timeZone)}
            aria-invalid={!!(value && !schedule?.ok)} aria-describedby="push-schedule-help"
            data-testid="push-schedule-input"
          />
          <div id="push-schedule-help" className="text-xs text-[hsl(var(--kb-muted))] mt-1" data-testid="push-schedule-tz">
            In the store's time zone (<b>{timeZone}</b>). At least 1 minute and at most {limits.max_horizon_days ?? MAX_HORIZON_DAYS} days ahead.
          </div>
          {value && !schedule?.ok && (
            <div className="text-xs text-red-600 mt-1" role="alert" data-testid="push-schedule-error">{schedule?.error}</div>
          )}
          {schedule?.ok && (
            <div className="text-xs mt-1" data-testid="push-schedule-resolved">
              Sends <b>{formatInZone(schedule.iso, timeZone)}</b>
              {schedule.notice && <div className="text-amber-700 mt-0.5" data-testid="push-schedule-notice">{schedule.notice}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HistoryList({ history, audienceLabel }) {
  return (
    <div className="bg-white border border-[hsl(var(--kb-border))] p-4 sm:p-6" data-testid="push-history">
      <h2 className="font-display text-xl mb-4">Recent broadcasts</h2>
      {history.length === 0 ? (
        <div className="text-sm text-[hsl(var(--kb-muted))]">No broadcasts yet.</div>
      ) : (
        <ul className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
          {history.map((b) => {
            const outcome = jobOutcome(b);
            return (
              <li key={b.id} className="border border-[hsl(var(--kb-border))] p-3 text-sm" data-testid={`push-history-${b.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium min-w-0 break-words [overflow-wrap:anywhere]">{b.title}</div>
                  <JobStatusBadge job={b} />
                </div>
                <div className="text-xs text-[hsl(var(--kb-muted))] mt-0.5 line-clamp-2 break-words [overflow-wrap:anywhere]">{b.body}</div>
                <div className="flex flex-wrap items-center gap-1.5 mt-2 text-xs">
                  <span className="bg-[hsl(var(--kb-surface-alt))] px-2 py-0.5 truncate max-w-full">{audienceLabel(b)}</span>
                  {b.kind === "scheduled" && <span className="bg-blue-50 text-blue-800 px-2 py-0.5">Scheduled</span>}
                  {b.source === "reengage" && <span className="bg-[hsl(var(--kb-surface-alt))] px-2 py-0.5">Re-engage</span>}
                  {b.delivery_mode === "sandbox" && <span className="bg-amber-50 text-amber-900 px-2 py-0.5">Sandbox</span>}
                </div>
                {outcome && <div className="text-xs mt-1.5 break-words [overflow-wrap:anywhere]" data-testid={`push-outcome-${b.id}`}>{outcome}{b.sent ? ` · ${b.sent} device${b.sent === 1 ? "" : "s"}` : ""}</div>}
                {jobIssue(b) && <div className="text-xs mt-1 text-red-700 break-words [overflow-wrap:anywhere]" data-testid={`push-issue-${b.id}`}>{jobIssue(b)}</div>}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-[hsl(var(--kb-muted))]">
                  <span className="inline-flex items-center gap-1" data-testid={`push-shown-${b.id}`}>
                    <Eye size={12} /> {b.shown_count || 0} shown
                  </span>
                  <span className="inline-flex items-center gap-1" data-testid={`push-clicks-${b.id}`}>
                    <MousePointerClick size={12} /> {b.click_count || 0} clicks
                  </span>
                  <span className="inline-flex items-center gap-1 text-[hsl(var(--kb-primary))] font-medium" data-testid={`push-ctr-${b.id}`}>
                    {(b.ctr || 0)}% CTR
                  </span>
                </div>
                <div className="text-[10px] text-[hsl(var(--kb-muted))] mt-1">{new Date(b.completed_at || b.created_at).toLocaleString()}</div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function AdminPushBroadcast() {
  const features = usePushFeatures();
  const [form, setForm] = useState({ title: "", body: "", url: "/", segment: "all_customers", segment_param: "" });
  const [mode, setMode] = useState(null); // "now" | "schedule"
  const [scheduleValue, setScheduleValue] = useState("");
  const [now, setNow] = useState(() => new Date());
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [scheduled, setScheduled] = useState({ items: [], loading: true, error: null });
  const [paramOptions, setParamOptions] = useState({ products: [], categories: [], creators: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [recurrence, setRecurrence] = useState(EMPTY_RECURRENCE);
  const [recPreview, setRecPreview] = useState({ loading: false, data: null, error: null });
  const [recurringItems, setRecurringItems] = useState([]);
  const [savingZone, setSavingZone] = useState(false);
  // Every schedule is entered and shown in the STORE's time zone (a tenant
  // setting), not the zone of whichever device the admin is using.
  const deviceTimeZone = useMemo(browserTimeZone, []);
  const timeZone = features.timezone || deviceTimeZone;
  const limits = features.limits || {};
  const titleMax = limits.title_max || 120;
  const bodyMax = limits.body_max || 400;

  const current = useMemo(() => SEGMENTS.find((s) => s.key === form.segment), [form.segment]);

  // Default to whichever mode this store is allowed to use.
  useEffect(() => {
    if (features.loading) return;
    setMode((m) => {
      if (m === "now" && features.bulk) return m;
      if ((m === "schedule" || m === "repeat") && features.scheduled) return m;
      return features.bulk ? "now" : features.scheduled ? "schedule" : null;
    });
  }, [features.loading, features.bulk, features.scheduled]);

  // Keep "too soon" validation honest while the form sits open.
  useEffect(() => {
    if (mode !== "schedule" && mode !== "repeat") return undefined;
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, [mode]);

  const schedule = useMemo(() => (mode === "schedule"
    ? validateSchedule(scheduleValue, {
      now, timeZone, minLeadSeconds: limits.min_lead_seconds ?? MIN_LEAD_SECONDS,
      maxHorizonDays: limits.max_horizon_days ?? MAX_HORIZON_DAYS,
    }) : null), [mode, scheduleValue, now, timeZone, limits.min_lead_seconds, limits.max_horizon_days]);

  // Recurring: default the start date to today (store time zone) the first
  // time the mode is opened, check the form locally, then ask the server
  // for the real first send times (it owns the time-zone arithmetic).
  const today = todayInZone(timeZone, now);
  useEffect(() => {
    if (mode === "repeat") setRecurrence((r) => (r.start_date ? r : { ...r, start_date: today }));
  }, [mode, today]);
  const recLocal = useMemo(() => (mode === "repeat"
    ? validateRecurrence(recurrence, { timeZone, now, maxOccurrences: limits.max_occurrences || 365 })
    : null), [mode, recurrence, timeZone, now, limits.max_occurrences]);
  const recKey = JSON.stringify(recurrencePayload(recurrence));
  useEffect(() => {
    if (mode !== "repeat" || !recLocal?.ok) {
      setRecPreview({ loading: false, data: null, error: null });
      return undefined;
    }
    let alive = true;
    setRecPreview((p) => ({ ...p, loading: true }));
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/admin/push/recurring/preview", JSON.parse(recKey));
        if (alive) setRecPreview({ loading: false, data, error: null });
      } catch (e) {
        if (alive) setRecPreview({ loading: false, data: null, error: apiErrorMessage(e, "Could not check this schedule") });
      }
    }, 350);
    return () => { alive = false; clearTimeout(t); };
    // `recKey` is the serialised rule; `timeZone` because the server uses the store's zone.
  }, [mode, recLocal?.ok, recKey, timeZone]);

  const loadAll = useCallback(async () => {
    try {
      const [h, s] = await Promise.all([api.get("/admin/push/broadcasts"), api.get("/admin/push/stats")]);
      setHistory(h.data.items || []);
      setStats(s.data);
      setLoadError(null);
    } catch (e) {
      setLoadError(apiErrorMessage(e, "Could not load broadcast data"));
    }
    if (features.scheduled) {
      try {
        const { data } = await api.get("/admin/push/scheduled");
        setScheduled({ items: data.items || [], loading: false, error: null });
      } catch (e) {
        setScheduled((s) => ({ ...s, loading: false, error: apiErrorMessage(e, "Could not load scheduled notifications") }));
      }
      try {
        const { data } = await api.get("/admin/push/recurring");
        setRecurringItems(data.items || []);
      } catch { /* the scheduled list above already reports a load problem */ }
    }
  }, [features.scheduled]);
  const loadAllRef = useRef(loadAll);
  loadAllRef.current = loadAll;

  const loadParamOptions = async () => {
    try {
      const [products, categories, creators] = await Promise.all([
        api.get("/products?limit=50&sort=popularity").catch(() => ({ data: { items: [] } })),
        api.get("/categories").catch(() => ({ data: [] })),
        api.get("/creators").catch(() => ({ data: [] })),
      ]);
      setParamOptions({
        products: products.data.items || [],
        categories: Array.isArray(categories.data) ? categories.data : (categories.data.items || []),
        creators: Array.isArray(creators.data) ? creators.data : [],
      });
    } catch (e) {
      console.warn("admin-push-options", e);
    }
  };

  useEffect(() => {
    if (features.loading || !features.anyEnabled) {
      if (!features.loading) setLoading(false);
      return undefined;
    }
    let alive = true;
    (async () => {
      await Promise.all([loadAll(), loadParamOptions()]);
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [features.loading, features.anyEnabled, loadAll]);

  // Live progress: poll quickly while something is sending, slowly while a
  // schedule is pending (so it flips to Sent without a manual refresh).
  const hasActive = history.some((b) => ACTIVE_STATUSES.includes(b.status))
    || scheduled.items.some((j) => ACTIVE_STATUSES.includes(j.status));
  const hasUpcoming = scheduled.items.some((j) => j.status === "scheduled")
    || recurringItems.some((s) => s.status === "active");
  useEffect(() => {
    const ms = hasActive ? 4000 : hasUpcoming ? 20000 : 0;
    if (!ms) return undefined;
    const t = setInterval(() => loadAllRef.current(), ms);
    return () => clearInterval(t);
  }, [hasActive, hasUpcoming]);

  // Re-run preview whenever the segment (or its param) changes
  useEffect(() => {
    if (!features.anyEnabled) return undefined;
    let alive = true;
    const run = async () => {
      if (current?.needsParam && !form.segment_param) {
        setPreview(null);
        return;
      }
      setPreviewing(true);
      try {
        const { data } = await api.get("/admin/push/segment-preview", {
          params: { segment: form.segment, param: form.segment_param || undefined },
        });
        if (alive) setPreview(data);
      } catch (e) {
        if (alive) setPreview({ error: apiErrorMessage(e, "Could not compute") });
      } finally {
        if (alive) setPreviewing(false);
      }
    };
    run();
    return () => { alive = false; };
  }, [form.segment, form.segment_param, current, features.anyEnabled]);

  const paramName = (b) => {
    const p = b.segment_param;
    if (!p) return "";
    const hit = paramOptions.products.find((x) => x.id === p)?.title
      || paramOptions.categories.find((x) => (x.id || x.slug) === p)?.name
      || paramOptions.creators.find((x) => x.id === p)?.display_name;
    return hit || String(p).slice(0, 8);
  };
  const audienceLabel = (b) => `${SEGMENT_LABEL[b.segment] || b.segment}${b.segment_param ? ` · ${paramName(b)}` : ""}`;

  const contentOk = form.title.trim() && form.body.trim() && (!current?.needsParam || form.segment_param);
  const reachKnown = !previewing && preview && !preview.error;
  const recurringReady = !!(recLocal?.ok && recPreview.data && !recPreview.loading);
  const canReview = !!(contentOk && reachKnown && !submitting && (
    mode === "now" ? preview.user_count > 0
      : mode === "schedule" ? schedule?.ok
        : mode === "repeat" ? recurringReady : false));

  const buildPayload = () => {
    const payload = {
      title: form.title.trim(),
      body: form.body.trim(),
      url: form.url.trim() || "/",
      segment: form.segment,
      segment_param: current?.needsParam ? form.segment_param : null,
    };
    if (mode === "schedule") Object.assign(payload, { scheduled_for: schedule.iso, timezone: timeZone });
    if (mode === "repeat") Object.assign(payload, recurrencePayload(recurrence));
    return payload;
  };

  const announce = (data) => {
    const reached = `${data.delivered_users ?? 0} of ${data.recipients_total ?? data.attempted ?? 0}`;
    if (data.frequency) {
      toast.success(`${data.replayed ? "Already scheduled" : "Recurring notification scheduled"} — first send ${formatInZone(data.first_run_at, data.timezone)}`);
    } else if (data.kind === "scheduled") {
      toast.success(`${data.replayed ? "Already scheduled" : "Scheduled"} for ${formatInZone(data.scheduled_for, data.timezone)}`);
    } else if (data.replayed) {
      toast.info("This notification was already sent — showing its result below.");
    } else if (data.status === "sent") {
      toast.success(`Sent — ${reached} people reached (${data.sent} device${data.sent === 1 ? "" : "s"})`);
    } else if (data.status === "partial") {
      toast.warning(`Partially sent — ${reached} people reached. See Recent broadcasts for details.`);
    } else if (data.status === "failed") {
      toast.error("Could not deliver to anyone. See Recent broadcasts for details.");
    } else {
      toast.success(`Sending to ${data.recipients_total ?? "your audience"} people in the background — progress shows under Recent broadcasts.`);
    }
  };

  const confirm = async () => {
    if (submitting) return;
    const payload = buildPayload();
    const key = idempotencyKeyFor(JSON.stringify([mode, payload]));
    setSubmitting(true);
    setSubmitError(null);
    try {
      const endpoint = { schedule: "/admin/push/scheduled", repeat: "/admin/push/recurring" }[mode] || "/admin/push/broadcast";
      const { data } = await api.post(endpoint, payload, { headers: { "Idempotency-Key": key } });
      clearIdempotencyKey();
      setReviewOpen(false);
      announce(data);
      setForm((f) => ({ ...f, title: "", body: "" }));
      setScheduleValue("");
      setRecurrence({ ...EMPTY_RECURRENCE, start_date: today });
      loadAll();
    } catch (e) {
      const status = e?.response?.status;
      const msg = apiErrorMessage(e, mode === "now" ? "Failed to send broadcast" : "Could not schedule");
      if (status === 409) {
        clearIdempotencyKey();
        setReviewOpen(false);
        toast.error(msg);
        loadAll();
      } else if (status === 403) {
        setSubmitError(msg);
        features.refresh();
      } else {
        // Keep the idempotency key: retrying replays, never double-sends.
        setSubmitError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const cancelJob = async (job) => {
    setCancellingId(job.id);
    try {
      await api.post(`/admin/push/scheduled/${job.id}/cancel`);
      toast.success("Scheduled notification cancelled");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not cancel"));
    } finally {
      setCancellingId(null);
      loadAll();
    }
  };

  const cancelSeries = async (series) => {
    setCancellingId(series.id);
    try {
      await api.post(`/admin/push/recurring/${series.id}/cancel`);
      toast.success("Recurring notification cancelled");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not cancel"));
    } finally {
      setCancellingId(null);
      loadAll();
    }
  };

  const saveTimeZone = async (zoneName) => {
    setSavingZone(true);
    try {
      await api.put("/admin/push/timezone", { timezone: zoneName });
      await features.refresh();
      toast.success(`Store time zone set to ${zoneName}`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not change the time zone"));
    } finally {
      setSavingZone(false);
    }
  };

  const paramInput = () => {
    if (!current?.needsParam) return null;
    const kind = current.paramKind;
    const common = {
      className: "w-full border border-[hsl(var(--kb-border))] p-2 text-sm bg-white min-h-[44px]",
      value: form.segment_param,
      onChange: (e) => setForm({ ...form, segment_param: e.target.value }),
    };
    if (kind === "product") {
      return (
        <select {...common} data-testid="push-param-product">
          <option value="">-- Select a product --</option>
          {paramOptions.products.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
      );
    }
    if (kind === "category") {
      return (
        <select {...common} data-testid="push-param-category">
          <option value="">-- Select a category --</option>
          {paramOptions.categories.map((c) => <option key={c.id || c.slug} value={c.id || c.slug}>{c.name || c.title}</option>)}
        </select>
      );
    }
    if (kind === "creator") {
      return (
        <select {...common} data-testid="push-param-creator">
          <option value="">-- Select a creator --</option>
          {paramOptions.creators.map((c) => <option key={c.id} value={c.id}>{c.display_name || c.handle}</option>)}
        </select>
      );
    }
    return null;
  };

  const header = (
    <div>
      <h1 className="font-display text-3xl">Push Broadcasts</h1>
      <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">
        Notify a targeted audience on the web and in your app — now or at a scheduled time.
        Respects each user's notification settings and never delivers the same notification twice.
      </p>
    </div>
  );

  if (features.loading) {
    return (
      <div className="space-y-8" data-testid="admin-push-broadcast">
        {header}
        <div className="h-48 bg-[hsl(var(--kb-surface-alt))] animate-pulse" data-testid="push-loading-skeleton" />
      </div>
    );
  }
  if (!features.anyEnabled) {
    return (
      <div className="space-y-8" data-testid="admin-push-broadcast">
        {header}
        {features.error
          ? <div className="border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm" data-testid="push-load-error">{features.error}</div>
          : <DisabledPanel />}
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-push-broadcast">
      {header}

      {features.appPushIssue && (
        <div className="border border-red-300 bg-red-50 text-red-900 px-4 py-3 text-sm flex gap-2" role="alert" data-testid="push-app-issue-banner">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <span className="min-w-0 break-words [overflow-wrap:anywhere]">
            <b>Notifications cannot reach your mobile app right now.</b> {features.appPushIssue} Browser
            notifications still work. Contact platform support to fix the app setup.
          </span>
        </div>
      )}
      {features.deliveryMode === "sandbox" && (
        <div className="border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 text-sm" data-testid="push-sandbox-banner">
          Sandbox delivery mode — notifications are recorded but not sent to real devices.
        </div>
      )}

      {loadError && (
        <div className="border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm flex items-start gap-2" data-testid="push-load-error">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>{loadError}</span>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="push-loading-skeleton">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-[92px] bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
          ))}
        </div>
      ) : stats ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
          <Stat label="Total broadcasts" value={stats.total_broadcasts} icon={Send} />
          <Stat label="Total delivered" value={stats.total_sent} icon={Check} />
          <Stat label="Total attempted" value={stats.total_attempted} icon={Users} />
          <Stat label="Total shown" value={stats.total_shown} icon={Eye} />
          <Stat label="Total clicks" value={stats.total_clicks} icon={MousePointerClick} />
          <Stat label="Overall CTR" value={`${stats.overall_ctr}%`} icon={Bell} />
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
        {/* Composer */}
        <div className="lg:col-span-3 bg-white border border-[hsl(var(--kb-border))] p-4 sm:p-6 space-y-4 min-w-0" data-testid="push-composer">
          <h2 className="font-display text-xl">Compose notification</h2>

          <div>
            <label className="text-sm font-medium" htmlFor="push-title">Title <span className="text-red-500">*</span></label>
            <input id="push-title"
              className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1 min-h-[44px]"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Mega Monsoon Sale is live"
              maxLength={titleMax}
              data-testid="push-title"
            />
            <div className="text-xs text-[hsl(var(--kb-muted))] mt-1">{form.title.length}/{titleMax}</div>
          </div>

          <div>
            <label className="text-sm font-medium" htmlFor="push-body">Message <span className="text-red-500">*</span></label>
            <textarea id="push-body"
              rows={3}
              className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Up to 70% off on smartphones, laptops and appliances. Tap to shop."
              maxLength={bodyMax}
              data-testid="push-body"
            />
            <div className="text-xs text-[hsl(var(--kb-muted))] mt-1">{form.body.length}/{bodyMax}</div>
          </div>

          <div>
            <label className="text-sm font-medium" htmlFor="push-url">Click-through URL</label>
            <input id="push-url"
              className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1 min-h-[44px]"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="/campaign/monsoon"
              data-testid="push-url"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Target audience</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
              {SEGMENTS.filter((s) => s.paramKind !== "creator" || paramOptions.creators.length > 0).map((s) => {
                const Icon = s.icon;
                const active = form.segment === s.key;
                return (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setForm({ ...form, segment: s.key, segment_param: "" })}
                    className={`flex items-start gap-2 border p-3 text-left text-sm transition ${active ? "border-[hsl(var(--kb-primary))] bg-[hsl(var(--kb-primary))]/5" : "border-[hsl(var(--kb-border))] hover:border-[hsl(var(--kb-primary))]/40"}`}
                    data-testid={`push-segment-${s.key}`}
                  >
                    <Icon size={16} className="mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium">{s.label}</div>
                      <div className="text-xs text-[hsl(var(--kb-muted))]">{s.helper}</div>
                    </div>
                  </button>
                );
              })}
            </div>
            {current?.needsParam && (
              <div className="mt-3">
                <label className="text-sm font-medium">Select {current.paramKind}</label>
                <div className="mt-1">{paramInput()}</div>
              </div>
            )}
          </div>

          {/* Live preview */}
          <div className="border border-dashed border-[hsl(var(--kb-border))] p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm" data-testid="push-preview">
            <div className="flex items-center gap-2">
              {previewing ? <Loader2 size={14} className="animate-spin" /> : <Users size={14} />}
              <span>
                {previewing ? "Calculating…" :
                  preview?.error ? <span className="text-red-600">{preview.error}</span> :
                  preview ? <>
                    <b data-testid="push-preview-count">{preview.user_count}</b> in this audience
                    {preview.reach && (
                      <span className="text-[hsl(var(--kb-muted))]" data-testid="push-preview-reach">
                        {" "}· <b className="text-[hsl(var(--kb-text))]">{preview.reach.reachable}</b> can receive it
                        {" "}(app {preview.reach.app}, browser {preview.reach.web}{preview.reach.opted_out ? `, ${preview.reach.opted_out} turned it off` : ""})
                      </span>
                    )}
                  </> :
                  "Select a segment to see reach"}
              </span>
            </div>
            {preview?.user_count === 0 && !previewing && !preview?.error && (
              <span className="flex items-center gap-1 text-amber-700 text-xs">
                <AlertTriangle size={12} className="shrink-0" />
                {mode === "now" ? "No one matches — nothing will be sent" : "No one matches yet — re-checked at send time"}
              </span>
            )}
          </div>

          {mode && (
            <WhenPicker features={features} mode={mode} setMode={setMode} value={scheduleValue}
              setValue={setScheduleValue} schedule={schedule} timeZone={timeZone} now={now}
              zone={<StoreTimeZone timeZone={timeZone} deviceTimeZone={deviceTimeZone} onChange={saveTimeZone} saving={savingZone} />}
              recurring={(
                <RecurrencePicker value={recurrence} onChange={setRecurrence} today={today} limits={limits}
                  maxStart={maxInputValue(now, limits.max_horizon_days ?? MAX_HORIZON_DAYS, timeZone).slice(0, 10)}
                  error={recLocal && !recLocal.ok ? recLocal.error : recPreview.error}
                  preview={recPreview.data} timeZone={timeZone} />
              )} />
          )}

          <button
            type="button"
            disabled={!canReview}
            onClick={() => { setSubmitError(null); setReviewOpen(true); }}
            className="kb-btn-primary inline-flex items-center justify-center gap-2 w-full sm:w-auto min-h-[44px]"
            data-testid="push-send-btn"
          >
            {mode === "repeat" ? <Repeat size={14} /> : mode === "schedule" ? <CalendarClock size={14} /> : <Send size={14} />}
            {mode === "now" ? "Review & send" : "Review & schedule"}
          </button>
        </div>

        <div className="lg:col-span-2 space-y-6 min-w-0">
          {features.scheduled && (
            <ScheduledList items={scheduled.items} loading={scheduled.loading} error={scheduled.error}
              audienceLabel={audienceLabel} onCancel={cancelJob} cancellingId={cancellingId}
              recurring={(
                <RecurringList items={recurringItems} audienceLabel={audienceLabel}
                  onCancel={cancelSeries} cancellingId={cancellingId} />
              )} />
          )}
          <HistoryList history={history} audienceLabel={audienceLabel} />
        </div>
      </div>

      {reviewOpen && (
        <ReviewDialog
          open={reviewOpen}
          onOpenChange={setReviewOpen}
          draft={{ title: form.title.trim(), body: form.body.trim(), url: form.url.trim() || "/" }}
          audienceLabel={audienceLabel({ segment: form.segment, segment_param: current?.needsParam ? form.segment_param : null })}
          reach={preview?.user_count ?? 0}
          when={mode === "schedule" ? schedule : null}
          recurring={mode === "repeat" && recPreview.data
            ? { rule: { ...recurrence, ...recurrencePayload(recurrence) }, preview: recPreview.data } : null}
          timeZone={timeZone}
          deliveryMode={features.deliveryMode}
          submitting={submitting}
          error={submitError}
          onConfirm={confirm}
        />
      )}
    </div>
  );
}
